import { useEffect, useRef } from 'react'
import { useI18n } from '../../i18n/I18nProvider'
import { Button } from './Button'

interface ConfirmDialogProps {
  open: boolean
  title: string
  /** Ce que l'action fait, en toutes lettres. Jamais « Êtes-vous sûr ? » seul. */
  message: string
  confirmLabel?: string
  busy?: boolean
  onConfirm: () => void
  onCancel: () => void
}

/**
 * Confirmation d'une action que l'utilisateur ne pourra pas défaire seul.
 *
 * Le message dit CE QUI va se passer et sur QUELLE fiche. « Êtes-vous sûr ? » ne
 * porte aucune information : l'utilisateur clique sans lire, et la confirmation ne
 * protège plus de rien.
 */
export function ConfirmDialog({
  open, title, message, confirmLabel, busy = false, onConfirm, onCancel,
}: ConfirmDialogProps) {
  const { t } = useI18n()
  const annuler = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return

    annuler.current?.focus()

    const surTouche = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
    }

    window.addEventListener('keydown', surTouche)
    return () => window.removeEventListener('keydown', surTouche)
  }, [open, onCancel])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="w-full max-w-md rounded-app border border-bordure bg-carte p-5 shadow-lg">
        <h2 className="mb-2 text-lg font-semibold">{title}</h2>
        <p className="mb-5 text-sm text-texte-doux">{message}</p>

        <div className="flex justify-end gap-2">
          <button ref={annuler} type="button" className="btn ghost" onClick={onCancel} disabled={busy}>
            {t('common.cancel')}
          </button>
          <Button variant="danger" onClick={onConfirm} disabled={busy}>
            {confirmLabel ?? t('common.confirm')}
          </Button>
        </div>
      </div>
    </div>
  )
}
